import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaCode, FaPalette, FaGlobe, FaPlayCircle, FaGraduationCap, FaTimes, FaExternalLinkAlt, FaArrowLeft } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';

// Assignment data. Titles and descriptions live in the locale files under
// `assignments.items.<id>`; categoryKey and tagKeys resolve against
// `assignments.categories.*` and `assignments.tags.*`.
const assignments = [
    {
        id: 1,
        categoryKey: 'cssBasics',
        icon: FaPalette,
        path: '/vazifa/vazifa1/vazifa_border.html',
        date: '2017',
        tagKeys: ['css', 'border', 'styling']
    },
    {
        id: 2,
        categoryKey: 'html',
        icon: FaCode,
        path: '/vazifa/vazifa1/vazifa_ro\'yhat.html',
        date: '2017',
        tagKeys: ['html', 'lists']
    },
    {
        id: 3,
        categoryKey: 'cssBasics',
        icon: FaPalette,
        path: '/vazifa/vazifa1/vazifa_tex_shadow.html',
        date: '2017',
        tagKeys: ['css', 'text', 'shadow']
    },
    {
        id: 4,
        categoryKey: 'website',
        icon: FaGlobe,
        path: '/vazifa/vazifa2_css_sodda_sayt/vazifa_sodda_sayt_doston.html',
        date: '2017',
        tagKeys: ['css', 'website']
    },
    {
        id: 5,
        categoryKey: 'animation',
        icon: FaPlayCircle,
        path: '/vazifa/vazifa_animatsiya_07.01/doston_sayt_css_animatsiya.html',
        date: '2017',
        tagKeys: ['css', 'animation', 'transition']
    },
    {
        id: 6,
        categoryKey: 'cssBasics',
        icon: FaPalette,
        path: '/vazifa/vazifa_gradient_07.01/gradient.html',
        date: '2017',
        tagKeys: ['css', 'gradient', 'colors']
    },
    {
        id: 7,
        categoryKey: 'website',
        icon: FaGlobe,
        path: '/vazifa/vazifa_css_sayt/doston_sayt_css.html',
        date: '2017',
        tagKeys: ['css', 'website', 'layout']
    },
    {
        id: 8,
        categoryKey: 'website',
        icon: FaGlobe,
        path: '/vazifa/vazifa_sayt_09.01/index.html',
        date: '2017',
        tagKeys: ['html', 'css', 'forms', 'multiPage']
    },
    {
        id: 9,
        categoryKey: 'framework',
        icon: FaGraduationCap,
        path: '/vazifa/Vazifa_less_1_14_01/index.html',
        date: '2017',
        tagKeys: ['less', 'preprocessor', 'css']
    },
    {
        id: 10,
        categoryKey: 'practice',
        icon: FaGraduationCap,
        path: '/vazifa/amaliyot-doston/index.html',
        date: '2017',
        tagKeys: ['practice', 'project']
    },
    {
        id: 11,
        categoryKey: 'personal',
        icon: FaCode,
        path: '/vazifa/Doston/index.html',
        date: '2017',
        tagKeys: ['personal', 'portfolio']
    },
    {
        id: 12,
        categoryKey: 'tool',
        icon: FaCode,
        path: '/vazifa/tarjima/latin_crill.html',
        date: '2017',
        tagKeys: ['javascript', 'translation', 'tool']
    }
];

// Category colors
const categoryColors = {
    cssBasics: { bg: 'from-purple-500 to-pink-500', text: 'purple' },
    html: { bg: 'from-orange-500 to-red-500', text: 'orange' },
    website: { bg: 'from-blue-500 to-cyan-500', text: 'blue' },
    animation: { bg: 'from-green-500 to-emerald-500', text: 'green' },
    framework: { bg: 'from-yellow-500 to-amber-500', text: 'yellow' },
    practice: { bg: 'from-indigo-500 to-violet-500', text: 'indigo' },
    personal: { bg: 'from-rose-500 to-pink-500', text: 'rose' },
    tool: { bg: 'from-teal-500 to-cyan-500', text: 'teal' }
};

const ALL_CATEGORIES = 'all';
const categoryKeys = [ALL_CATEGORIES, ...new Set(assignments.map(a => a.categoryKey))];

const AssignmentsPage = () => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);
    const [previewAssignment, setPreviewAssignment] = useState(null);

    const filteredAssignments = selectedCategory === ALL_CATEGORIES
        ? assignments
        : assignments.filter(a => a.categoryKey === selectedCategory);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: [0.6, -0.05, 0.01, 0.99]
            }
        }
    };

    return (
        <div className="min-h-screen py-8">
            <div className="section-container">
                {/* Back Button */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-8"
                >
                    <Link
                        to="/"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isDarkMode
                            ? 'bg-dark/60 text-primary hover:bg-dark border border-primary/20'
                            : 'bg-white text-green-600 hover:bg-gray-50 border border-gray-200'
                            } transition-all duration-300 shadow-md`}
                    >
                        <FaArrowLeft className="rtl:rotate-180" /> {t('assignments.back')}
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className={`${isDarkMode ? 'bg-dark-overlay' : 'bg-white'} backdrop-blur-md rounded-2xl p-8 md:p-12 shadow-xl`}
                >
                    {/* Header */}
                    <div className="text-center mb-12">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDarkMode
                                ? 'bg-gradient-to-r from-primary to-secondary'
                                : 'bg-gradient-to-r from-green-400 to-emerald-500'
                                }`}
                        >
                            <FaGraduationCap className="text-4xl text-white" />
                        </motion.div>
                        <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isDarkMode ? 'text-primary' : 'text-green-600'
                            }`}>
                            {t('assignments.title')}
                        </h1>
                        <p className={`text-lg md:text-xl ${isDarkMode ? 'text-light/70' : 'text-gray-600'} max-w-2xl mx-auto`}>
                            {t('assignments.subtitle')}
                        </p>
                    </div>

                    {/* Category Filter */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-wrap justify-center gap-3 mb-10"
                    >
                        {categoryKeys.map((category) => (
                            <motion.button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${selectedCategory === category
                                    ? isDarkMode
                                        ? 'bg-primary text-dark shadow-lg shadow-primary/30'
                                        : 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                                    : isDarkMode
                                        ? 'bg-dark/50 text-light/70 hover:bg-dark hover:text-primary border border-primary/20'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                                    }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {category === ALL_CATEGORIES
                                    ? t('assignments.all')
                                    : t(`assignments.categories.${category}`)}
                            </motion.button>
                        ))}
                    </motion.div>

                    {/* Assignments Grid */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        <AnimatePresence mode="wait">
                            {filteredAssignments.map((assignment) => {
                                const IconComponent = assignment.icon;
                                const colors = categoryColors[assignment.categoryKey] || categoryColors.cssBasics;

                                return (
                                    <motion.div
                                        key={assignment.id}
                                        variants={cardVariants}
                                        layout
                                        className={`group relative overflow-hidden rounded-xl ${isDarkMode
                                            ? 'bg-dark/60 hover:bg-dark/80 border border-primary/10 hover:border-primary/30'
                                            : 'bg-white hover:bg-gray-50 border border-gray-100 hover:border-gray-200'
                                            } transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer`}
                                        onClick={() => setPreviewAssignment(assignment)}
                                        whileHover={{ y: -5 }}
                                    >
                                        {/* Gradient Top Bar */}
                                        <div className={`h-2 w-full bg-gradient-to-r ${colors.bg}`} />

                                        <div className="p-6">
                                            {/* Icon and Category */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className={`p-3 rounded-xl bg-gradient-to-r ${colors.bg} shadow-lg`}>
                                                    <IconComponent className="text-2xl text-white" />
                                                </div>
                                                <span className={`text-xs font-medium px-3 py-1 rounded-full ${isDarkMode
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {t(`assignments.categories.${assignment.categoryKey}`)}
                                                </span>
                                            </div>

                                            {/* Title and Description */}
                                            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-light' : 'text-gray-800'
                                                } group-hover:${isDarkMode ? 'text-primary' : 'text-green-600'} transition-colors`}>
                                                {t(`assignments.items.${assignment.id}.title`)}
                                            </h3>
                                            <p className={`text-sm mb-4 ${isDarkMode ? 'text-light/60' : 'text-gray-500'
                                                }`}>
                                                {t(`assignments.items.${assignment.id}.description`)}
                                            </p>

                                            {/* Tags */}
                                            <div className="flex flex-wrap gap-2 mb-4">
                                                {assignment.tagKeys.map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className={`text-xs px-2 py-1 rounded ${isDarkMode
                                                            ? 'bg-dark text-light/50'
                                                            : 'bg-gray-100 text-gray-500'
                                                            }`}
                                                    >
                                                        {t(`assignments.tags.${tag}`)}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* Footer */}
                                            <div className="flex items-center justify-between pt-4 border-t border-dashed border-opacity-20 border-gray-400">
                                                <span className={`text-xs ${isDarkMode ? 'text-light/40' : 'text-gray-400'}`}>
                                                    📅 {assignment.date}
                                                </span>
                                                <motion.span
                                                    className={`text-sm font-medium ${isDarkMode ? 'text-primary' : 'text-green-600'
                                                        } flex items-center gap-1`}
                                                    whileHover={{ x: 5 }}
                                                >
                                                    {t('assignments.view')} <FaExternalLinkAlt className="text-xs" />
                                                </motion.span>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </motion.div>

                    {/* Stats */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4"
                    >
                        {[
                            { label: t('assignments.stats.total'), value: assignments.length, icon: '📚' },
                            { label: t('assignments.stats.categories'), value: categoryKeys.length - 1, icon: '📁' },
                            { label: t('assignments.stats.websites'), value: assignments.filter(a => a.categoryKey === 'website').length, icon: '🌐' },
                            { label: t('assignments.stats.cssProjects'), value: assignments.filter(a => a.tagKeys.includes('css')).length, icon: '🎨' }
                        ].map((stat, index) => (
                            <motion.div
                                key={index}
                                className={`text-center p-6 rounded-xl ${isDarkMode
                                    ? 'bg-dark/40 border border-primary/10'
                                    : 'bg-gray-50 border border-gray-100'
                                    }`}
                                whileHover={{ scale: 1.05 }}
                            >
                                <div className="text-3xl mb-2">{stat.icon}</div>
                                <div className={`text-3xl md:text-4xl font-bold ${isDarkMode ? 'text-primary' : 'text-green-600'
                                    }`}>
                                    {stat.value}
                                </div>
                                <div className={`text-sm mt-1 ${isDarkMode ? 'text-light/60' : 'text-gray-500'}`}>
                                    {stat.label}
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </motion.div>
            </div>

            {/* Preview Modal */}
            <AnimatePresence>
                {previewAssignment && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                        onClick={() => setPreviewAssignment(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className={`relative w-full max-w-6xl h-[85vh] rounded-2xl overflow-hidden ${isDarkMode ? 'bg-dark' : 'bg-white'
                                } shadow-2xl`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-primary/20 bg-dark-overlay' : 'border-gray-200 bg-gray-50'
                                }`}>
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg bg-gradient-to-r ${categoryColors[previewAssignment.categoryKey]?.bg || 'from-purple-500 to-pink-500'
                                        }`}>
                                        {React.createElement(previewAssignment.icon, { className: 'text-lg text-white' })}
                                    </div>
                                    <div>
                                        <h3 className={`font-bold ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>
                                            {t(`assignments.items.${previewAssignment.id}.title`)}
                                        </h3>
                                        <p className={`text-sm ${isDarkMode ? 'text-light/60' : 'text-gray-500'}`}>
                                            {t(`assignments.categories.${previewAssignment.categoryKey}`)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <motion.a
                                        href={previewAssignment.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isDarkMode
                                            ? 'bg-primary text-dark hover:bg-secondary'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                            } transition-colors font-medium text-sm`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <FaExternalLinkAlt /> {t('assignments.openNewWindow')}
                                    </motion.a>
                                    <motion.button
                                        onClick={() => setPreviewAssignment(null)}
                                        className={`p-2 rounded-lg ${isDarkMode
                                            ? 'text-light/60 hover:text-light hover:bg-dark'
                                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                            } transition-colors`}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <FaTimes className="text-xl" />
                                    </motion.button>
                                </div>
                            </div>

                            {/* iFrame */}
                            <iframe
                                src={previewAssignment.path}
                                className="w-full h-full border-0"
                                title={t(`assignments.items.${previewAssignment.id}.title`)}
                                sandbox="allow-same-origin allow-scripts"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AssignmentsPage;
